import { defineStore, storeToRefs } from "pinia";
import { ref } from "vue";
import { useCommon } from "../composition-functions/common";
import { OffsetBasedPaginationQueryResult } from "../dtos/PaginationQueryResult";
import { IQueryParams } from "../interfaces/IParams";
import { useMandant } from "../mandant/service.mandant";
import { NotificationDto } from "../notification/dto.notification";
import { shouldShowSensitiveMandantData } from "../users/service.user";

let interval: undefined | ReturnType<typeof setTimeout> = undefined;

export async function refreshUnreadNotificationCountForCurrentMandant(): Promise<{ success: boolean }> {
  const mandantStore = useMandant();
  const { currentMandant } = storeToRefs(mandantStore);
  const notificationStore = useNotifications();
  const { setUnreadNotificationCount } = notificationStore;
  if (!interval) {
    interval = setInterval(refreshUnreadNotificationCountForCurrentMandant, 1000 * 60);
  }
  if (!currentMandant.value || !shouldShowSensitiveMandantData()) {
    setUnreadNotificationCount(0);
    return { success: true };
  }

  const { getRequest } = useCommon();
  try {
    const response = await getRequest({ path: `/admin/mandants/${currentMandant.value.id}/notifications/unreadCount` });
    const result = await response.json();
    setUnreadNotificationCount(result);
    return { success: true };
  } catch (_error) {
    return { success: false };
  }
}

type GetNotificationParams = { mandantId: number; queryParams: IQueryParams };
export async function getNotifications({
  mandantId,
  queryParams,
}: GetNotificationParams): Promise<OffsetBasedPaginationQueryResult<NotificationDto>> {
  const { getRequest } = useCommon();

  const urlSearchParams = new URLSearchParams();

  urlSearchParams.append("searchTerm", queryParams.searchTerm);
  urlSearchParams.append("currentPage", queryParams.currentPage.toString());
  urlSearchParams.append("pageSize", queryParams.pageSize.toString());

  const response = await getRequest({ path: `/admin/mandants/${mandantId}/notifications`, urlSearchParams });
  const result = await response.json();
  return result;
}

type SetNoticationFlagParams = { mandantId: number; notificationIds: number[] };

type SetWasReadForNotificationsParams = { wasRead: boolean; keepalive?: boolean } & SetNoticationFlagParams;
export async function setWasReadForNotifications({
  mandantId,
  notificationIds,
  wasRead,
  keepalive,
}: SetWasReadForNotificationsParams): Promise<NotificationDto[]> {
  const { putRequest } = useCommon();

  const response = await putRequest({
    path: `/admin/mandants/${mandantId}/notifications/setWasRead`,
    data: JSON.stringify({ notificationIds, wasRead }),
    keepalive,
  });
  const result = await response.json();

  refreshUnreadNotificationCountForCurrentMandant();

  return result;
}

export async function deleteNotifications({ notificationIds, mandantId }: SetNoticationFlagParams): Promise<void> {
  const { deleteRequest } = useCommon();

  await deleteRequest({
    path: `/admin/mandants/${mandantId}/notifications`,
    data: JSON.stringify({ notificationIds }),
  });

  refreshUnreadNotificationCountForCurrentMandant();

  return;
}

export const useNotifications = defineStore("notificationStore", () => {
  const unreadNotificationCount = ref<null | number>(null);

  function setUnreadNotificationCount(count: null | number) {
    unreadNotificationCount.value = count;
  }

  return {
    unreadNotificationCount,
    getNotifications,
    deleteNotifications,
    refreshUnreadNotificationCountForCurrentMandant,
    setWasReadForNotifications,
    setUnreadNotificationCount,
  };
});
